import { ArrowUpRight, Check, Star } from "lucide-react";

const services = [
  {
    id: "essential",
    num: "01",
    kicker: "Essential",
    title: "Site One Page",
    pitch: "Pour démarrer rapidement.",
    description:
      "Une seule page pensée comme un parcours fluide : votre activité, votre offre, votre contact — tout au même endroit, sans distraction.",
    bullets: [
      "1 page longue, structurée en sections claires",
      "Présentation de votre activité + galerie",
      "Formulaire de contact intégré",
      "100 % responsive (mobile, tablette, desktop)",
      "Référencement Google local optimisé",
    ],
    meta: {
      priceOneShot: "990 €",
      priceOneShotSuffix: "HTVA · paiement unique",
      delay: "2 à 3 semaines",
    },
    image: "/service-essential.webp",
  },
  {
    id: "evolution",
    num: "02",
    kicker: "Evolution",
    title: "Site Multi-pages",
    pitch: "Pour développer votre présence.",
    description:
      "Un site complet, structuré en plusieurs pages, pour présenter en profondeur votre activité et développer votre visibilité en ligne.",
    bullets: [
      "5 à 8 pages (Accueil, Services, À propos, Contact…)",
      "Blog ou espace actualités inclus",
      "Fonctionnalités avancées (galerie, formulaires…)",
      "Référencement Google renforcé",
      "Architecture pensée pour évoluer",
    ],
    meta: {
      priceOneShot: "1 790 €",
      priceOneShotSuffix: "HTVA · paiement unique",
      delay: "3 à 4 semaines",
    },
    image: "/service-evolution.webp",
    featured: true,
    featuredLabel: "Le plus populaire",
  },
  {
    id: "premium",
    num: "03",
    kicker: "Premium",
    title: "Identité Digitale Complète",
    pitch: "Pour marquer les esprits.",
    description:
      "Un écosystème digital complet : site multi-pages, identité de marque et présence en ligne, pensés ensemble pour vous démarquer partout où vos clients vous cherchent.",
    bullets: [
      "Site multi-pages sur mesure",
      "Logo & charte graphique",
      "Fiche Google Business créée & optimisée",
      "Réseaux sociaux configurés (Instagram, Facebook…)",
      "Accompagnement stratégique de lancement",
    ],
    meta: {
      priceOneShot: "2 790 €",
      priceOneShotSuffix: "HTVA · paiement unique",
      delay: "4 à 6 semaines",
    },
    image: "/service-premium.webp",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      {/* Header */}
      <div className="mx-auto max-w-7xl px-6 md:px-10 mb-16 md:mb-24">
        {/* Founder pricing badge — subtle urgency signal */}
        <div className="mb-6" data-testid="services-founder-badge-wrapper">
          <span
            data-testid="services-founder-badge"
            className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#0B1027] font-bold shadow-tactile"
            style={{
              boxShadow:
                "0 0 0 1px rgba(168,85,247,0.28), 0 2px 8px rgba(11,16,39,0.06)",
            }}
          >
            <span
              className="text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
              }}
            >
              ✦
            </span>
            Tarifs de lancement · 2026
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">— Ce que je crée</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05]">
              Trois solutions pour
              <br />
              <em className="not-italic italic font-light text-aurora">
                développer votre présence numérique.
              </em>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[#0F172A]/75 leading-relaxed text-base md:text-lg">
              <strong className="font-semibold text-[#0B1027]">
                Étape 1 : Choisissez le format qui correspond à votre projet.
              </strong>{" "}
              Ensuite seulement, vous déciderez si vous souhaitez un
              accompagnement mensuel.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: horizontal snap scroll · Desktop: 3-column grid */}
      <div
        data-testid="services-list"
        className="
          flex md:grid md:grid-cols-3 md:gap-7 md:items-stretch md:pt-6
          gap-5 overflow-x-auto md:overflow-visible
          snap-x snap-mandatory md:snap-none
          px-6 md:px-10 pb-6 md:pb-0
          mx-auto max-w-7xl
          scroll-pl-6
          [scrollbar-width:none] [-ms-overflow-style:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {services.map((s) => (
          <ServiceCard key={s.id} service={s} />
        ))}
      </div>

      {/* Bridge to subscription — step 2 nudge, low-key */}
      <div className="mx-auto max-w-7xl px-6 md:px-10 mt-10 md:mt-14">
        <div
          data-testid="services-bridge-banner"
          className="relative overflow-hidden rounded-2xl p-5 md:p-6"
          style={{
            background:
              "linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(168,85,247,0.06) 50%, rgba(236,72,153,0.06) 100%)",
          }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{
              padding: "1px",
              background:
                "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              opacity: 0.35,
            }}
          />
          <p className="relative text-sm md:text-[15px] leading-relaxed text-[#0F172A]">
            <strong className="font-semibold text-[#0B1027]">
              Étape 2 (optionnelle) :
            </strong>{" "}
            Une fois votre site en ligne, choisissez si vous souhaitez que je
            continue à m'en occuper —{" "}
            <a
              href="#abonnement"
              data-testid="services-bridge-link"
              className="font-semibold text-[#6366F1] underline underline-offset-4 decoration-[#A855F7]/40 hover:decoration-[#A855F7] transition"
            >
              découvrir l'Accompagnement Numedia ↓
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  const featured = !!service.featured;
  return (
    <article
      data-testid={`service-card-${service.id}`}
      className={`group relative flex flex-col rounded-3xl overflow-hidden bg-white hover-lift transition-shadow
        snap-start shrink-0 w-[82%] sm:w-[58%] md:w-auto
        ${featured ? "shadow-tactile-lg md:-translate-y-3" : "shadow-tactile"}`}
    >
      {/* Aurora gradient border — always on featured, on hover for others */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded-3xl z-20 transition-opacity duration-300 ${
          featured ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
        style={{
          padding: "2px",
          background:
            "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />
      {/* Kicker badge — placed above the image, on the card top */}
      <div className="pt-5 px-5 pb-3 flex justify-center">
        <span className="relative inline-flex items-center rounded-full bg-white px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0B1027] font-bold">
          {service.kicker}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              padding: "1px",
              background:
                "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />
        </span>
      </div>
      {/* Image — uniform 5:4 ratio + cream backdrop (matches every visual) */}
      <div className="relative aspect-[5/4] overflow-hidden bg-[#F9F6F4] mx-5 rounded-2xl">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        {service.meta && (
          <div
            data-testid={`service-card-${service.id}-meta`}
            className="absolute bottom-0 left-0 right-0 px-4 py-3 md:px-5 md:py-3.5 flex flex-col gap-2 text-white"
            style={{
              background:
                "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
            }}
          >
            {featured && (
              <div className="flex items-center gap-1.5 pb-1.5 border-b border-white/20">
                <Star size={11} className="fill-white text-white" />
                <span
                  data-testid={`service-card-${service.id}-featured-badge`}
                  className="text-[10px] uppercase tracking-[0.18em] font-bold text-white leading-none"
                >
                  {service.featuredLabel}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-baseline gap-2 whitespace-nowrap">
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-white/80">
                  Création
                </span>
                <span className="font-display text-[15px] md:text-lg text-white leading-none font-medium">
                  {service.meta.priceOneShot}
                </span>
              </div>
              <div className="flex items-baseline gap-2 whitespace-nowrap">
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-white/80">
                  Délai
                </span>
                <span className="font-display text-[13px] md:text-sm text-white leading-none">
                  {service.meta.delay}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-7 md:p-8">
        <h3 className="font-display text-2xl md:text-[1.65rem] tracking-tight font-medium text-[#0B1027] leading-[1.15]">
          {service.title}
        </h3>
        <p className="mt-3 font-display italic text-aurora text-lg leading-snug">
          {service.pitch}
        </p>
        {service.description && (
          <p className="mt-3 text-[0.93rem] leading-relaxed text-[#0F172A]/75 md:min-h-[8.5rem]">
            {service.description}
          </p>
        )}

        <ul className="mt-6 pt-6 border-t border-[#0B1027]/10 space-y-2.5">
          {service.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-3 text-sm text-[#0F172A]"
            >
              <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full btn-aurora text-white shrink-0">
                <Check size={11} strokeWidth={3} />
              </span>
              {b}
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6 border-t border-[#0B1027]/10">
          <a
            href="#reservation"
            data-testid={`service-card-${service.id}-cta`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full btn-aurora text-white font-semibold px-5 h-12 text-sm shadow-tactile hover-lift transition whitespace-nowrap group/cta"
          >
            Réserver un appel
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
            />
          </a>
        </div>
      </div>
    </article>
  );
}
